import React, { useState, useEffect } from 'react';
import { 
  Gift, Plus, Trash2, ToggleLeft, ToggleRight, 
  Percent, DollarSign, Calendar, Users, Copy, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: 100,
    expiry_date: '',
    is_active: true,
    min_purchase: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.promo_codes || []);
      }
    } catch (error) {
      console.error('Failed to load promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newCode.code) {
      toast({
        title: "Error",
        description: "Please enter a promo code",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCode)
      });
      
      if (response.ok) {
        toast({
          title: "Promo Code Created",
          description: `Code "${newCode.code.toUpperCase()}" has been created`,
        });
        setShowCreate(false);
        setNewCode({
          code: '',
          discount_type: 'percentage',
          discount_value: 10,
          max_uses: 100,
          expiry_date: '',
          is_active: true,
          min_purchase: 0
        });
        loadPromoCodes();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create promo code",
        variant: "destructive"
      });
    }
  };

  const handleToggleCode = async (codeId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${codeId}?is_active=${!currentStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        loadPromoCodes();
      }
    } catch (error) {
      console.error('Failed to toggle code:', error);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Are you sure you want to delete this promo code?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/promo-codes/${codeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: "Promo Code Deleted" });
        loadPromoCodes();
      }
    } catch (error) {
      console.error('Failed to delete code:', error);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewCode(prev => ({ ...prev, code }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Promo Codes</h1>
          <p className="text-gray-600">Create and manage discount codes</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-mint-500 hover:bg-mint-600">
          <Plus className="w-4 h-4 mr-2" />
          Create Code
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Gift className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Active Codes</p>
                <p className="text-2xl font-bold">{promoCodes.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Uses</p>
                <p className="text-2xl font-bold">{promoCodes.reduce((sum, c) => sum + (c.current_uses || 0), 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Codes</p>
                <p className="text-2xl font-bold">{promoCodes.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Code Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Create Promo Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Code</Label>
                <div className="flex gap-2">
                  <Input
                    value={newCode.code}
                    onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    placeholder="SUMMER20"
                    className="font-mono uppercase"
                  />
                  <Button variant="outline" onClick={generateRandomCode}>
                    Generate
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Discount Type</Label>
                  <select
                    value={newCode.discount_type}
                    onChange={(e) => setNewCode(prev => ({ ...prev, discount_type: e.target.value }))}
                    className="w-full h-10 px-3 border rounded-md"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount ($)</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label>Discount Value</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={newCode.discount_value}
                      onChange={(e) => setNewCode(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                      {newCode.discount_type === 'percentage' ? '%' : '$'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max Uses</Label>
                  <Input
                    type="number"
                    value={newCode.max_uses}
                    onChange={(e) => setNewCode(prev => ({ ...prev, max_uses: parseInt(e.target.value) || 100 }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date</Label>
                  <Input
                    type="date"
                    value={newCode.expiry_date}
                    onChange={(e) => setNewCode(prev => ({ ...prev, expiry_date: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleCreateCode} className="flex-1 bg-mint-500 hover:bg-mint-600">
                  Create Code
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Promo Codes List */}
      <Card>
        <CardHeader>
          <CardTitle>All Promo Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No promo codes yet</p>
              <p className="text-sm">Create your first promo code to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {promoCodes.map((promo) => {
                const isExpired = promo.expiry_date && new Date(promo.expiry_date) < new Date();
                const usagePercent = (promo.current_uses / promo.max_uses) * 100;
                
                return (
                  <div 
                    key={promo.id}
                    className={`border rounded-lg p-4 ${!promo.is_active || isExpired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <code className="text-lg font-bold bg-gray-100 px-3 py-1 rounded">
                            {promo.code}
                          </code>
                          <button
                            onClick={() => copyCode(promo.code)}
                            className="p-1 hover:bg-gray-100 rounded"
                          >
                            {copiedCode === promo.code ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-400" />
                            )}
                          </button>
                        </div>
                        
                        <div className="flex items-center gap-1 text-lg font-semibold text-green-600">
                          {promo.discount_type === 'percentage' ? (
                            <>
                              <Percent className="w-4 h-4" />
                              {promo.discount_value}% OFF
                            </>
                          ) : (
                            <>
                              <DollarSign className="w-4 h-4" />
                              {promo.discount_value} OFF
                            </>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {isExpired && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                            Expired
                          </span>
                        )}
                        
                        <button
                          onClick={() => handleToggleCode(promo.id, promo.is_active)}
                          className="p-1 hover:bg-gray-100 rounded"
                        >
                          {promo.is_active ? (
                            <ToggleRight className="w-8 h-8 text-green-500" />
                          ) : (
                            <ToggleLeft className="w-8 h-8 text-gray-400" />
                          )}
                        </button>
                        
                        <button
                          onClick={() => handleDeleteCode(promo.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 flex items-center gap-6 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>{promo.current_uses || 0} / {promo.max_uses} uses</span>
                      </div>
                      {promo.expiry_date && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Expires: {new Date(promo.expiry_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Usage bar */}
                    <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-mint-500 rounded-full transition-all"
                        style={{ width: `${Math.min(usagePercent, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPromoCodes;
